import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { fetchFlowCmiCasesRequest } from "../../app/actions/flowCmiActions";
import { startLoadingFlowEngineCatalogs } from "../../app/actions/flowEngineActions";
import { startLoadingCatalogRoles } from "../../app/actions/catalogRoleActions";
import { startLoadingCatalogCiiusByLevel1 } from "../../app/actions/catalogCiiuActions";
import { startLoadingCatalogsAll } from "../../app/actions/catalogActions";
import { listFlowRoleUsers } from "../../app/actions/userSearchActions";
import { listMyFlowCoverageGroups } from "../../app/actions/flowCoverageActions";
import { startLogout } from "../../app/actions/authActions";
import { selectSpecialistRoleToWork } from "../../app/actions/userRoleActions";
import { RolesEnum } from "../../app/types/RolesEnum";
import {
  isFlowEngineEnabledForProcess,
  canInboxEdit,
  canInboxReassign,
  canPoolOperate,
  canPoolView,
  FLOW_MODULE_DEFINITIONS,
  getModuleDisplayLabel
} from "../../app/helpers/flowEngineConfigHelper";
import {
  resolveModuleAccessForRole,
  hasFlowInboxOverride,
  normalizeSpecialistRoleKey
} from "../../app/helpers/activeSpecialistRoleHelper";
import {
  EMPTY_CMI_AGGREGATES,
  flowCaseKey,
  formatFilteredPair,
  formatSpecialistUserLabel,
  summarizeCaseMassSelection
} from "../../app/helpers/flowCaseMassHelper";
import NavbarIconsModuleContext from "../generic-nav-bar/NavbarIconsModuleContext";
import { MassiveModuleDownloadButton } from "../specialist-flow-engine/components/MassiveModuleDownloadModal";
import Pagination from "../generic-ui/Pagination";
import Select from "../generic-ui/Select";
import { openFlowModuleFicha } from "../../app/helpers/flowCmiOpenRecordHelper";
import { FLOW_ORIGIN_NAV, MODULE_FICHA_NAV, buildFichaNavState } from "../../app/helpers/moduleFichaNavHelper";
import { startDeleteModuleRecord } from "../../app/helpers/moduleRecordDeleteHelper";
import { canSoftDeleteFlowRecord, resolveAuthUserId } from "../../app/helpers/flowEditPageHelper";
import useActiveSpecialistRole from "../../app/hooks/useActiveSpecialistRole";
import FlowStateSemaphore from "../specialist-flow-engine/components/FlowStateSemaphore";
import TrayVisionName from "../specialist-flow-engine/TrayVisionName";
import FlowCaseFilterBar from "../specialist-flow-engine/FlowCaseFilterBar";
import FlowCaseMassToolbar from "../specialist-flow-engine/FlowCaseMassToolbar";
import FlowMassReassignPanel from "../specialist-flow-engine/FlowMassReassignPanel";
import RecordExcelUpdateRowButton from "../specialist-flow-engine/components/RecordExcelUpdateRowButton";
import { getCasePhaseMeta, ALL_PHASE_FILTER_OPTIONS, MANAGER_PHASE_FILTER_OPTIONS, resolveCasePhase } from "../../app/helpers/flowCasePhaseHelper";
import {
  TRAY_TAB_MODE,
  TRAY_VIEW,
  canOperateTrayView,
  defaultTrayView,
  isAssignmentTabRole,
  resolveTrayTabMode
} from "../../app/helpers/flowTraySurfaceHelper";
import { loadTrayBoardState, saveTrayBoardState } from "../../app/helpers/flowTrayFilterPersistHelper";
import {
  getRoleDisplayLabel,
  sortRolesByDisplayOrder,
  canonicalizeSpecialistRoleName
} from "../../app/helpers/specialistRoleModuleHelper";

const CIRCUIT_TRAY_ROLE_NAMES = new Set([
  RolesEnum.SPECIALIST_PRE_MANAGER,
  RolesEnum.SPECIALIST_MANAGER,
  RolesEnum.SPECIALIST_VALIDATOR,
  RolesEnum.SPECIALIST_VERIFICATOR,
  RolesEnum.SPECIALIST_EDITOR
]);

const CIRCUIT_USER_ROLE_KEYS = ["pre-manager", "manager", "validator", "verificator", "editor"];

const circuitUserRoleKeysForTray = (roleName = "") => {
  const canonical = canonicalizeSpecialistRoleName(roleName);
  if (canonical === RolesEnum.SPECIALIST_PRE_MANAGER) return ["pre-manager"];
  if (canonical === RolesEnum.SPECIALIST_MANAGER) return ["manager"];
  if (canonical === RolesEnum.SPECIALIST_VALIDATOR) return ["validator", "verificator"];
  if (canonical === RolesEnum.SPECIALIST_EDITOR) return ["editor"];
  return CIRCUIT_USER_ROLE_KEYS;
};

const PAGE_SIZE_OPTIONS = [2, 3, 4, 5, 10, 15, 20, 25, 50, 100];

const EMPTY_MODULE_ACCESS = { allowed: [], orphan: [], visible: [] };

const EMPTY_TRAY_FILTERS = {
  process: "",
  text: "",
  state_catalog_id: "",
  current_user_id: "",
  catalogciius_id: "",
  catalogserie_id: "",
  flow_coverage_group_id: "",
  from: "",
  to: "",
  published_status: "",
  visibility_status: "",
  case_phase: "",
  case_phase_group: "",
  tray_role_catalog_id: "",
  assignment_status: ""
};

const COORDINATOR_PUBLISHED_STATUSES = new Set(["published", "unpublished", "eliminated"]);
const VISIBILITY_STATUSES = new Set(["visible", "hidden"]);

const normalizeOperatorTrayFilters = (surface = "", filters = {}) => {
  const next = { ...filters };
  if (surface === "liaison") {
    next.published_status = "published";
    if (!VISIBILITY_STATUSES.has(next.visibility_status)) {
      next.visibility_status = "visible";
    }
    return next;
  }
  if (surface === "coordinator") {
    if (!COORDINATOR_PUBLISHED_STATUSES.has(next.published_status)) {
      next.published_status = "published";
    }
    if (next.published_status === "published") {
      if (!VISIBILITY_STATUSES.has(next.visibility_status)) {
        next.visibility_status = "visible";
      }
    } else {
      next.visibility_status = "";
    }
    if (next.published_status !== "unpublished") {
      next.tray_role_catalog_id = "";
      next.assignment_status = "";
    }
  }
  return next;
};

const MODULE_OPTIONS = [
  { key: "technology", labelKey: "modules.technology" },
  { key: "alternative", labelKey: "modules.alternative" },
  { key: "experience", labelKey: "modules.experience" },
  { key: "innovation", labelKey: "modules.innovation" },
  { key: "assistance", labelKey: "modules.assistance" },
  { key: "financial", labelKey: "modules.financial" },
  { key: "vision", labelKey: "modules.vision" },
  { key: "regulation", labelKey: "modules.regulation" }
];

const resolveRoleValue = (value = "") => {
  const raw = (value || "").trim();
  if (Object.values(RolesEnum).includes(raw)) return raw;
  const lower = raw.toLowerCase();
  if (lower === "manager" || lower === "specialist-manager") return RolesEnum.SPECIALIST_MANAGER;
  if (lower === "validator" || lower === "specialist-validator") return RolesEnum.SPECIALIST_VALIDATOR;
  if (lower === "editor" || lower === "specialist-editor") return RolesEnum.SPECIALIST_EDITOR;
  if (lower === "pre-manager" || lower === "specialist-pre-manager") return RolesEnum.SPECIALIST_PRE_MANAGER;
  if (lower === "coordinator" || lower === "specialist-coordinator") return RolesEnum.SPECIALIST_COORDINATOR;
  if (lower === "liaison" || lower === "specialist-liaison" || lower === "enlace") return RolesEnum.SPECIALIST_LIAISON;
  return raw;
};

const handleTokenError = (body, dispatch) => {
  if (body?.code === "bak-aut-tok-inv-001" || body?.code === "bak-aut-tok-inv-002") {
    Swal.fire("Error", "Sesión inválida", "error");
    dispatch(startLogout());
    return true;
  }
  return false;
};

const SpecialistAssignmentBoard = ({ roleKey, titleKey, defaultTitle, consultativeMode }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    canDelete,
    canCreate,
    selectedRole,
    flowEngineEnabled,
    availableRoles
  } = useActiveSpecialistRole();

  const normalizedRole = useMemo(() => resolveRoleValue(roleKey), [roleKey]);
  const { roleLogin, roleSelected } = useSelector((state) => state.userRole);
  const configuration = useSelector((state) => state.configuration);
  const authUser = useSelector((state) => state.auth?.user);
  const authUserId = resolveAuthUserId(authUser);
  const { states: flowStates = [] } = useSelector((state) => state.flowEngine);
  const catalogRoles = useSelector((state) => state.catalogRole?.catalogRoles || []);
  const { listCatalogCiiusLevel1 = [] } = useSelector((state) => state.catalogCiiu);
  const { listSeries = [] } = useSelector((state) => state.catalog);
  const actionLoading = useSelector((state) => state.specialistSelection?.actionLoading || {});

  const flowEnabledModules = useMemo(
    () => MODULE_OPTIONS
      .filter((item) => isFlowEngineEnabledForProcess(configuration, item.key))
      .map((item) => item.key),
    [configuration]
  );

  const allowedModules = useMemo(() => {
    const moduleAccessByRole = roleLogin?.moduleAccessByRole || {};
    const activeRole = roleLogin?.roleSpecialistSelected;
    let roleModules = roleSelected?.moduleAccess || roleLogin?.moduleAccess;

    if ((!Array.isArray(roleModules) || roleModules.length === 0) && activeRole?.id) {
      roleModules = resolveModuleAccessForRole(moduleAccessByRole, activeRole);
    }

    const moduleKeys = Array.isArray(roleModules)
      ? roleModules.map((item) => String(item).toLowerCase())
      : [];

    return moduleKeys.filter((key) => flowEnabledModules.includes(key));
  }, [
    roleLogin,
    roleSelected?.moduleAccess,
    flowEnabledModules
  ]);

  const isAdministrator = hasFlowInboxOverride(roleLogin);
  const inboxSurface = useMemo(() => {
    const lower = String(normalizedRole || "").toLowerCase();
    if (lower.includes("pre-manager") || lower.includes("pre-gestor")) return "pre-manager";
    if (lower.includes("coordinator") || lower.includes("coordinador")) return "coordinator";
    if (lower.includes("liaison") || lower.includes("enlace")) return "liaison";
    if (lower.includes("manager")) return "manager";
    if (lower.includes("validator") || lower.includes("verificator") || lower.includes("verificador")) return "validator";
    if (lower.includes("editor")) return "editor";
    return "";
  }, [normalizedRole]);
  const isManagerTray = inboxSurface === "manager";
  const isPreManagerTray = inboxSurface === "pre-manager";
  const assignmentTabSurface = isAssignmentTabRole(inboxSurface);

  const roleCatalog = useMemo(
    () => catalogRoles.find((role) => role.name === normalizedRole)
      || catalogRoles.find((role) => {
        const key = String(role.name || "").toLowerCase();
        const wanted = String(normalizedRole || "").toLowerCase();
        if (wanted.includes("validator") || wanted.includes("verificator")) {
          return key.includes("verificator") || key.includes("validator");
        }
        if (wanted.includes("liaison")) {
          return key.includes("liaison");
        }
        return key === wanted;
      })
      || null,
    [catalogRoles, normalizedRole]
  );
  const roleId = roleCatalog?.id || "";
  const trayRoleAutoSyncedRef = useRef("");

  useEffect(() => {
    if (!inboxSurface || trayRoleAutoSyncedRef.current === inboxSurface) {
      return;
    }
    if (!availableRoles.length || !roleCatalog?.id) {
      return;
    }
    const match = availableRoles.find((role) => Number(role.id) === Number(roleCatalog.id));
    trayRoleAutoSyncedRef.current = inboxSurface;
    if (!match || Number(selectedRole?.id) === Number(match.id)) {
      return;
    }
    dispatch(selectSpecialistRoleToWork(match));
  }, [availableRoles, dispatch, inboxSurface, roleCatalog]);

  const [filters, setFilters] = useState({ ...EMPTY_TRAY_FILTERS });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [recordKind, setRecordKind] = useState("all");
  const restoredSurfaceRef = useRef("");
  const skipSaveRef = useRef(true);
  const [boardReady, setBoardReady] = useState(false);
  const [restoreTick, setRestoreTick] = useState(0);
  const [page, setPage] = useState(0);
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [aggregates, setAggregates] = useState(EMPTY_CMI_AGGREGATES);
  const [universe, setUniverse] = useState(EMPTY_CMI_AGGREGATES);
  const [universeFacets, setUniverseFacets] = useState(null);
  const [moduleAccess, setModuleAccess] = useState(EMPTY_MODULE_ACCESS);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [coverageGroups, setCoverageGroups] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [trayView, setTrayView] = useState(TRAY_VIEW.MINE);
  const tabMode = resolveTrayTabMode(inboxSurface);

  const allowedSet = useMemo(
    () => new Set((moduleAccess.allowed || []).length ? moduleAccess.allowed : allowedModules),
    [moduleAccess.allowed, allowedModules]
  );
  const orphanSet = useMemo(() => new Set(moduleAccess.orphan || []), [moduleAccess.orphan]);
  const visibleModules = useMemo(() => {
    const fromServer = (moduleAccess.visible || []).filter((key) => flowEnabledModules.includes(key));
    return fromServer.length ? fromServer : allowedModules;
  }, [allowedModules, flowEnabledModules, moduleAccess.visible]);
  const firstAllowed = [...allowedSet][0];
  const sampleProcess = (filters.process && allowedSet.has(filters.process))
    ? filters.process
    : (firstAllowed || "technology");
  const selectedIsOrphanModule = Boolean(
    filters.process && orphanSet.has(filters.process) && !allowedSet.has(filters.process)
  );
  const selectedRoleKey = normalizeSpecialistRoleKey(selectedRole);
  const effectiveRoleKey = selectedRoleKey || inboxSurface;
  const canViewPool = canPoolView(configuration, inboxSurface, effectiveRoleKey);
  const canOperatePool = canPoolOperate(configuration, inboxSurface, selectedRoleKey);
  const consultativeFromFlag = !canInboxEdit(configuration, sampleProcess, inboxSurface, isAdministrator);
  const canReassignFlag = consultativeMode === undefined
    ? canInboxReassign(configuration, selectedRoleKey)
    : !consultativeMode;
  const isConsultative = consultativeMode === undefined
    ? (consultativeFromFlag && !canReassignFlag) || !canOperatePool
    : consultativeMode || !canOperatePool;
  const isPoolView = trayView === TRAY_VIEW.POOL;
  const showMineTab = tabMode === TRAY_TAB_MODE.THREE;
  const showPoolTab = tabMode === TRAY_TAB_MODE.THREE || tabMode === TRAY_TAB_MODE.POOL_GENERAL;
  const showTrayTabs = showMineTab || showPoolTab;
  const canOperateOnView = showTrayTabs
    ? canOperateTrayView({
      trayView,
      selectedRoleKey,
      canOperatePool
    })
    : canOperatePool;
  const canOperateRecord = (row) => canOperateOnView && allowedSet.has(row.process);
  const showMass = !isConsultative && canOperateOnView && cases.some((row) => allowedSet.has(row.process));

  const circuitTrayRoles = useMemo(() => {
    const rows = (catalogRoles || []).filter((role) => CIRCUIT_TRAY_ROLE_NAMES.has(role.name));
    const hasVerificator = rows.some((role) => role.name === RolesEnum.SPECIALIST_VERIFICATOR);
    const filtered = hasVerificator
      ? rows.filter((role) => role.name !== RolesEnum.SPECIALIST_VALIDATOR)
      : rows;
    return sortRolesByDisplayOrder(filtered);
  }, [catalogRoles]);

  const showCoordinatorUnpublishedFilters = inboxSurface === "coordinator"
    && filters.published_status === "unpublished";

  const reassignUsers = useMemo(() => {
    if (!showCoordinatorUnpublishedFilters || !selectedRecords.length) {
      return users;
    }
    const roleIds = [...new Set(
      selectedRecords.map((row) => Number(row.current_role_catalog_id)).filter(Boolean)
    )];
    if (roleIds.length !== 1) {
      return users;
    }
    const [targetRoleId] = roleIds;
    const targetRole = catalogRoles.find((role) => Number(role.id) === targetRoleId);
    return users.filter((user) => (
      (user.catalogrole_ids || []).some((id) => Number(id) === targetRoleId)
      || Boolean(targetRole && (user.roles || []).includes(targetRole.name))
    ));
  }, [catalogRoles, selectedRecords, showCoordinatorUnpublishedFilters, users]);

  const hasFilters = Boolean(
    filters.process
    || filters.text
    || filters.state_catalog_id
    || filters.current_user_id
    || filters.catalogciius_id
    || filters.catalogserie_id
    || filters.flow_coverage_group_id
    || filters.from
    || filters.to
    || filters.case_phase_group
    || filters.case_phase
    || filters.tray_role_catalog_id
    || filters.assignment_status
  );

  const moduleSelectOptions = useMemo(() => ([
    { key: "", label: t("flow.cmi.all", "Todos") },
    ...MODULE_OPTIONS
      .filter((item) => visibleModules.includes(item.key))
      .map((item) => ({
        key: item.key,
        label: orphanSet.has(item.key) && !allowedSet.has(item.key)
          ? `${t(item.labelKey)} ${t("flow.cmi.module-orphan-suffix", "(solo lectura)")}`
          : t(item.labelKey)
      }))
  ]), [allowedSet, orphanSet, t, visibleModules]);

  const trayOrigin = FLOW_ORIGIN_NAV[inboxSurface] || {
    returnTo: location.pathname,
    returnLabelKey: titleKey,
    returnLabel: t(titleKey, defaultTitle)
  };

  const traySearchBase = useMemo(() => {
    if (assignmentTabSurface && trayView === TRAY_VIEW.POOL) {
      return { claimPool: true, current_user_id: "", current_role_catalog_id: roleId };
    }
    const base = { current_role_catalog_id: roleId };
    if (inboxSurface === "coordinator") {
      base.coordinatorInbox = true;
      return base;
    }
    if (inboxSurface === "liaison") {
      base.liaisonInbox = true;
      return base;
    }
    if (assignmentTabSurface && trayView === TRAY_VIEW.GENERAL) {
      base.assigned_scope = "any";
      return base;
    }
    if (assignmentTabSurface && trayView === TRAY_VIEW.MINE && authUserId) {
      base.current_user_id = authUserId;
    }
    return base;
  }, [assignmentTabSurface, authUserId, inboxSurface, roleId, trayView]);

  const loadUniverse = useCallback(async () => {
    if (!canViewPool || (!roleId && !isPoolView)) {
      setUniverse(EMPTY_CMI_AGGREGATES);
      setUniverseFacets(null);
      setModuleAccess(EMPTY_MODULE_ACCESS);
      return;
    }
    const body = await fetchFlowCmiCasesRequest({
      ...traySearchBase,
      published_status: filters.published_status,
      visibility_status: filters.visibility_status,
      ...(inboxSurface === "pre-manager" ? { record_kind: recordKind || "all" } : {}),
      pool_surface: inboxSurface,
      active_catalogrole_id: selectedRole?.id || roleId,
      active_role_key: effectiveRoleKey,
      page: 0,
      size: 1
    });
    if (!body?.ok) {
      if (!handleTokenError(body, dispatch) && body?.msg) {
        Swal.fire(t("titles.error"), body.msg, "error");
      }
      return;
    }
    setUniverse(body.aggregates || EMPTY_CMI_AGGREGATES);
    setUniverseFacets(body.facets || null);
    setModuleAccess(body.module_access || EMPTY_MODULE_ACCESS);
  }, [canViewPool, dispatch, effectiveRoleKey, filters.published_status, filters.visibility_status, inboxSurface, isPoolView, recordKind, roleId, selectedRole?.id, t, traySearchBase]);

  const runSearch = useCallback(async (pageArg = 0, sizeArg = itemsPerPage, filtersOverride = null) => {
    if (!canViewPool || (!roleId && !isPoolView)) {
      setCases([]);
      setTotal(0);
      setAggregates(EMPTY_CMI_AGGREGATES);
      setModuleAccess(EMPTY_MODULE_ACCESS);
      return;
    }
    const activeFilters = filtersOverride || filters;
    setLoading(true);
    try {
      const body = await fetchFlowCmiCasesRequest({
        ...activeFilters,
        ...traySearchBase,
        ...(inboxSurface === "pre-manager" ? { record_kind: recordKind || "all" } : {}),
        pool_surface: inboxSurface,
        active_catalogrole_id: selectedRole?.id || roleId,
        active_role_key: effectiveRoleKey,
        page: pageArg,
        size: sizeArg
      });
      if (!body?.ok) {
        if (!handleTokenError(body, dispatch) && body?.msg) {
          Swal.fire(t("titles.error"), body.msg, "error");
        }
        return;
      }
      setCases(body.cases || []);
      setTotal(body.total || 0);
      setPage(body.page || 0);
      setAggregates(body.aggregates || EMPTY_CMI_AGGREGATES);
      setModuleAccess(body.module_access || EMPTY_MODULE_ACCESS);
      setSelectedRecords([]);
    } finally {
      setLoading(false);
    }
  }, [canViewPool, dispatch, effectiveRoleKey, filters, inboxSurface, isPoolView, itemsPerPage, recordKind, roleId, selectedRole?.id, t, traySearchBase]);

  useEffect(() => {
    dispatch(startLoadingCatalogsAll());
    dispatch(startLoadingFlowEngineCatalogs());
    dispatch(startLoadingCatalogRoles());
    dispatch(startLoadingCatalogCiiusByLevel1(1));
  }, [dispatch]);

  useEffect(() => {
    if (!inboxSurface) {
      return;
    }
    const moduleId = FLOW_MODULE_DEFINITIONS[filters.process]?.moduleId;
    let roleKeys = inboxSurface === "validator"
      ? ["validator", "verificator"]
      : [inboxSurface];
    if (showCoordinatorUnpublishedFilters) {
      const selectedTray = circuitTrayRoles.find(
        (role) => Number(role.id) === Number(filters.tray_role_catalog_id)
      );
      roleKeys = selectedTray
        ? circuitUserRoleKeysForTray(selectedTray.name)
        : CIRCUIT_USER_ROLE_KEYS;
    }
    listFlowRoleUsers(roleKeys, moduleId).then(setUsers);
  }, [
    inboxSurface,
    filters.process,
    filters.tray_role_catalog_id,
    showCoordinatorUnpublishedFilters,
    circuitTrayRoles
  ]);

  useEffect(() => {
    const role = selectedRoleKey || inboxSurface;
    if (!role || role === "coordinator" || role === "liaison") {
      setCoverageGroups([]);
      return undefined;
    }
    const moduleId = FLOW_MODULE_DEFINITIONS[filters.process]?.moduleId;
    let cancelled = false;
    const params = { role };
    if (moduleId) {
      params.catalogmodule_id = moduleId;
    }
    listMyFlowCoverageGroups(params).then((rows) => {
      if (!cancelled) {
        setCoverageGroups(rows);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [filters.process, selectedRoleKey, inboxSurface]);

  useEffect(() => {
    if (!roleId || !boardReady) {
      return;
    }
    loadUniverse();
    runSearch(0, itemsPerPage);
    // Initial load when the tray role, pool tab or Pre assignee filter is resolved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId, trayView, traySearchBase, filters.published_status, filters.visibility_status, recordKind, boardReady, restoreTick]);

  useEffect(() => {
    if (!inboxSurface) {
      return;
    }
    const saved = loadTrayBoardState(inboxSurface);
    if (saved?.filters) {
      setFilters(normalizeOperatorTrayFilters(inboxSurface, { ...EMPTY_TRAY_FILTERS, ...saved.filters }));
    } else {
      setFilters(normalizeOperatorTrayFilters(inboxSurface, { ...EMPTY_TRAY_FILTERS }));
    }
    if (saved?.trayView && inboxSurface !== "coordinator") {
      setTrayView(saved.trayView);
    } else {
      setTrayView(defaultTrayView(inboxSurface));
    }
    if (saved?.itemsPerPage) {
      setItemsPerPage(saved.itemsPerPage);
    }
    if (typeof saved?.advancedOpen === "boolean") {
      setAdvancedOpen(saved.advancedOpen);
    }
    if (saved?.recordKind === "term" || saved?.recordKind === "dri" || saved?.recordKind === "all") {
      setRecordKind(saved.recordKind);
    } else if (inboxSurface === "pre-manager") {
      setRecordKind("all");
    }
    setSelectedRecords([]);
    restoredSurfaceRef.current = inboxSurface;
    skipSaveRef.current = true;
    setBoardReady(true);
    setRestoreTick((tick) => tick + 1);
  }, [inboxSurface]);

  useEffect(() => {
    if (!inboxSurface || restoredSurfaceRef.current !== inboxSurface) {
      return;
    }
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    saveTrayBoardState(inboxSurface, {
      filters,
      trayView,
      itemsPerPage,
      advancedOpen,
      recordKind
    });
  }, [inboxSurface, filters, trayView, itemsPerPage, advancedOpen, recordKind]);

  const refreshAll = () => {
    loadUniverse();
    runSearch(page, itemsPerPage);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    runSearch(0, itemsPerPage);
  };

  const applyFiltersAndSearch = (nextFilters) => {
    setFilters(nextFilters);
    runSearch(0, itemsPerPage, nextFilters);
  };

  const handleClearFilters = () => {
    applyFiltersAndSearch(normalizeOperatorTrayFilters(inboxSurface, {
      ...EMPTY_TRAY_FILTERS,
      published_status: filters.published_status,
      visibility_status: filters.visibility_status
    }));
  };

  const applyPublishedSlice = (publishedStatus) => {
    applyFiltersAndSearch(normalizeOperatorTrayFilters(inboxSurface, {
      ...filters,
      published_status: publishedStatus
    }));
  };

  const applyVisibilitySlice = (visibilityStatus) => {
    applyFiltersAndSearch({
      ...filters,
      visibility_status: visibilityStatus
    });
  };

  const applyTrayRoleFilter = (trayRoleId) => {
    applyFiltersAndSearch({
      ...filters,
      tray_role_catalog_id: trayRoleId,
      current_user_id: filters.assignment_status === "unassigned" ? "" : filters.current_user_id
    });
  };

  const applyAssignmentStatusFilter = (status) => {
    applyFiltersAndSearch({
      ...filters,
      assignment_status: status,
      current_user_id: status === "unassigned" ? "" : filters.current_user_id
    });
  };

  const handleFilterBarChange = (nextFilters) => {
    if (nextFilters.assignment_status === "unassigned" && nextFilters.current_user_id) {
      setFilters({ ...nextFilters, assignment_status: "" });
      return;
    }
    setFilters(nextFilters);
  };

  const handleClearField = (field) => {
    applyFiltersAndSearch({ ...filters, [field]: "" });
  };

  const onPageChange = (pageClick) => {
    runSearch(pageClick - 1, itemsPerPage);
  };

  const onChangeSelect = (event) => {
    const nextSize = Number(event.target.value) || 10;
    setItemsPerPage(nextSize);
    runSearch(0, nextSize);
  };

  const stateLabel = (id) => flowStates.find((item) => Number(item.id) === Number(id))?.name || id || "—";

  const userLabel = (id) => {
    if (!id) {
      return showCoordinatorUnpublishedFilters
        ? t("flow.pool.filter.pool-badge", "Pool")
        : "—";
    }
    const found = users.find((user) => Number(user.id) === Number(id));
    return found ? formatSpecialistUserLabel(found) : `#${id}`;
  };

  const trayLabel = (row) => {
    if (!row?.current_role_catalog_id) {
      return t("flow.cmi.out-of-tray", "Fuera de bandeja");
    }
    const found = catalogRoles.find((role) => Number(role.id) === Number(row.current_role_catalog_id));
    return found ? getRoleDisplayLabel(found.name, t) : row.current_role_catalog_id;
  };

  const selectTrayView = (nextView) => {
    if (trayView === nextView) {
      return;
    }
    setTrayView(nextView);
    setSelectedRecords([]);
    if (nextView !== TRAY_VIEW.GENERAL) {
      setFilters((prev) => ({ ...prev, current_user_id: "" }));
    }
  };

  const openFicha = (record) => {
    if (record?.subject_kind === "search_key_term") {
      navigate(`/specialist-feeder/term/${record.id}`, {
        state: {
          keepRole: true,
          returnTo: trayOrigin.returnTo || location.pathname,
          trayView: showTrayTabs ? trayView : undefined
        }
      });
      return;
    }
    openFlowModuleFicha(record.process, record, dispatch, navigate, {
      origin: inboxSurface,
      returnTo: trayOrigin.returnTo || location.pathname,
      returnLabelKey: trayOrigin.returnLabelKey || titleKey,
      returnLabel: trayOrigin.returnLabel || t(titleKey, defaultTitle),
      trayView: showTrayTabs ? trayView : undefined
    });
  };

  const handleDeleteRecord = (record) => {
    dispatch(startDeleteModuleRecord(record.process, record.id, () => refreshAll()));
  };

  const canDeleteRecord = (record) => canOperateRecord(record) && canSoftDeleteFlowRecord({
    record,
    flowEngineEnabled,
    canDelete,
    selectedRole,
    flowEngine: { states: flowStates }
  });

  const selectedKeys = useMemo(() => new Set(selectedRecords.map(flowCaseKey)), [selectedRecords]);
  const operableCases = cases.filter((row) => canOperateRecord(row));
  const allPageSelected = operableCases.length > 0
    && operableCases.every((row) => selectedKeys.has(flowCaseKey(row)));
  const massSummary = useMemo(
    () => summarizeCaseMassSelection(selectedRecords, { roleKey: inboxSurface }),
    [inboxSurface, selectedRecords]
  );
  const reassignProcess = massSummary.process || sampleProcess;
  const selectedTrayRole = useMemo(() => {
    if (!massSummary.roleId) {
      return null;
    }
    return catalogRoles.find((role) => Number(role.id) === Number(massSummary.roleId)) || null;
  }, [catalogRoles, massSummary.roleId]);
  const canReassign = canOperatePool
    && canInboxReassign(configuration, selectedRoleKey);

  const toggleRow = (row) => {
    const key = flowCaseKey(row);
    setSelectedRecords((prev) => (
      prev.some((item) => flowCaseKey(item) === key)
        ? prev.filter((item) => flowCaseKey(item) !== key)
        : [...prev, row]
    ));
  };

  const togglePage = () => {
    const operableRows = cases.filter((row) => canOperateRecord(row));
    if (allPageSelected) {
      const pageKeys = new Set(operableRows.map(flowCaseKey));
      setSelectedRecords((prev) => prev.filter((item) => !pageKeys.has(flowCaseKey(item))));
      return;
    }
    setSelectedRecords((prev) => {
      const next = [...prev];
      const keys = new Set(prev.map(flowCaseKey));
      operableRows.forEach((row) => {
        if (!keys.has(flowCaseKey(row))) {
          next.push(row);
        }
      });
      return next;
    });
  };

  const cards = [
    {
      label: t("flow.cmi.total", "Total de casos"),
      value: formatFilteredPair(aggregates?.total ?? total, universe?.total || 0, hasFilters)
    },
    {
      label: t("flow.cmi.modules", "Módulos"),
      value: formatFilteredPair(
        aggregates?.modulesWithResults || 0,
        universe?.modulesWithResults || 0,
        hasFilters
      )
    },
    {
      label: t("specialist.selection.metrics.totalPending", "Pendientes / bloqueados"),
      value: formatFilteredPair(aggregates?.pending || 0, universe?.pending || 0, hasFilters)
    },
    {
      label: t("flow.cmi.role-users", "Usuarios del rol"),
      value: formatFilteredPair(
        aggregates?.usersWithCases || 0,
        users.length || universe?.usersWithCases || 0,
        hasFilters
      )
    }
  ];

  const title = t(titleKey, defaultTitle);
  const createModule = (filters.process && allowedSet.has(filters.process))
    ? filters.process
    : (firstAllowed || allowedModules[0] || "technology");
  const createNav = MODULE_FICHA_NAV[createModule];

  const handleCreateDri = () => {
    if (!createNav?.newPath) {
      return;
    }
    navigate(createNav.newPath, {
      state: buildFichaNavState(createModule, {
        returnTo: trayOrigin.returnTo || location.pathname,
        returnLabelKey: trayOrigin.returnLabelKey || titleKey,
        returnLabel: trayOrigin.returnLabel || title
      })
    });
  };

  return (
    <>
      <NavbarIconsModuleContext
        moduleProcess={createModule}
        mode="workspace"
        returnTo={trayOrigin.returnTo}
        returnLabelKey={trayOrigin.returnLabelKey}
        returnLabel={trayOrigin.returnLabel}
      />
      <div className="container-fluid py-3">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
          <div>
            <h3 className="mb-1">{title}</h3>
            <p className="text-muted mb-0">
              {t(
                inboxSurface === "coordinator"
                  ? "specialist.selection.coordinator.subtitle"
                  : inboxSurface === "liaison"
                    ? "specialist.selection.liaison.subtitle"
                    : isPreManagerTray
                      ? "specialist.selection.pre-manager.subtitle"
                      : "specialist.selection.subtitle",
                inboxSurface === "coordinator"
                  ? "Una sola bandeja general. Publicados (pausar, reanudar o dar de baja), no publicados en cualquier bandeja o pool, y dados de baja."
                  : inboxSurface === "liaison"
                    ? "Solo casos publicados. Filtra por visibles o no visibles (pausados). El Enlace no ve casos en flujo ni dados de baja."
                    : isPreManagerTray
                      ? "Tema-contenido asignado en tu bandeja. Crea el DRI, verifica los datos y envía a la bandeja de gestores."
                      : "Casos actuales de esta bandeja. Filtra por usuario, módulo y estado; reasigna solo con el mismo CIIU y cadena."
              )}
            </p>
            <Link to="/specialist-flow-cmi" className="small">
              {t("flow.cmi.open-from-tray", "Abrir panel de control de todos los casos")}
            </Link>
            {!canViewPool && (
              <div className="alert alert-warning py-2 small mt-3 mb-0">
                {t(
                  "flow.pool.view-denied",
                  "El rol activo no puede consultar esta bandeja. Cambia el rol activo en la barra superior."
                )}
              </div>
            )}
            {canViewPool && !canOperatePool && (
              <div className="alert alert-info py-2 small mt-3 mb-0">
                {t(
                  "flow.pool.operate-denied",
                  "El rol activo solo puede consultar esta bandeja. Para operar, selecciona el rol de esta bandeja (o Coordinador) arriba."
                )}
              </div>
            )}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {isPreManagerTray && canCreate && createNav?.newPath && (
              <button type="button" className="btn btn-primary" onClick={handleCreateDri}>
                <i className="fa fa-plus me-2" />
                {t("specialist.selection.create-dri", "Nuevo DRI")}
              </button>
            )}
            <button type="button" className="btn btn-outline-secondary" onClick={refreshAll}>
              <i className="fa fa-sync me-2" />
              {t("buttons.refresh", "Actualizar")}
            </button>
          </div>
        </div>

        <div className="row g-3 mb-4">
          {cards.map((card) => (
            <div className="col-md-3" key={card.label}>
              <div className="card">
                <div className="card-body">
                  <div className="text-muted small">{card.label}</div>
                  <div className="fs-3 fw-semibold">{loading ? "…" : card.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <FlowCaseFilterBar
          filters={filters}
          onChange={handleFilterBarChange}
          onSearch={handleSearch}
          loading={loading}
          modules={moduleSelectOptions}
          states={flowStates}
          users={users}
          ciius={(listCatalogCiiusLevel1 || []).filter((item) => !item.parent_id)}
          series={listSeries}
          showTrayFilter={false}
          showUserFilter={!showTrayTabs || trayView === TRAY_VIEW.GENERAL}
          showPhaseFilter={isManagerTray || inboxSurface === "liaison" || inboxSurface === "coordinator"}
          phaseOptions={isManagerTray
            ? MANAGER_PHASE_FILTER_OPTIONS
            : (inboxSurface === "liaison" || inboxSurface === "coordinator" ? ALL_PHASE_FILTER_OPTIONS : [])}
          phaseInAdvanced
          showClearFilters
          showFilterMarkers
          availableFacets={universeFacets}
          onClearFilters={handleClearFilters}
          onClearField={handleClearField}
          userFirstRow
          advancedOpen={advancedOpen}
          onToggleAdvanced={() => setAdvancedOpen((open) => !open)}
          coverageGroups={coverageGroups}
        />
        {selectedIsOrphanModule && (
          <div className="alert alert-warning py-2 small">
            {t(
              "flow.cmi.module-readonly",
              "No tiene permiso para operar en este módulo. Puede consultar los casos asignados a usted, en solo lectura."
            )}
          </div>
        )}
        {inboxSurface === "coordinator" && (
          <div className="mb-3">
            <div className="d-flex flex-wrap gap-2">
              <div className="btn-group btn-group-sm" role="group" aria-label={t("flow.pool.filter.published-group", "Publicación")}>
                <button type="button" className={`btn ${filters.published_status === "published" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => applyPublishedSlice("published")}>
                  {t("flow.pool.filter.published", "Publicados")}
                </button>
                <button type="button" className={`btn ${filters.published_status === "unpublished" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => applyPublishedSlice("unpublished")}>
                  {t("flow.pool.filter.unpublished", "No publicados")}
                </button>
                <button type="button" className={`btn ${filters.published_status === "eliminated" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => applyPublishedSlice("eliminated")}>
                  {t("flow.pool.filter.eliminated", "Eliminados")}
                </button>
              </div>
              {filters.published_status === "published" && (
                <div className="btn-group btn-group-sm" role="group" aria-label={t("flow.pool.filter.visibility-group", "Visibilidad")}>
                  <button type="button" className={`btn ${filters.visibility_status === "visible" ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => applyVisibilitySlice("visible")}>
                    {t("flow.pool.filter.visible", "Visibles")}
                  </button>
                  <button type="button" className={`btn ${filters.visibility_status === "hidden" ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => applyVisibilitySlice("hidden")}>
                    {t("flow.pool.filter.hidden", "No visibles")}
                  </button>
                </div>
              )}
            </div>
            <p className="small text-muted mb-0 mt-2">
              {filters.published_status === "unpublished"
                ? t("flow.pool.filter.hint-unpublished", "Casos en flujo, en cualquier bandeja o pool, asignados o sin asignar. No incluye dados de baja.")
                : filters.published_status === "eliminated"
                  ? t("flow.pool.filter.hint-eliminated", "Dados de baja. Ya no están en el catálogo ni en el flujo.")
                  : t("flow.pool.filter.hint-published", "Ya publicados. Visibles llegan al usuario final; no visibles están pausados.")}
            </p>
            {showCoordinatorUnpublishedFilters && (
              <div className="row g-3 mt-1">
                <div className="col-md-3">
                  <label className="form-label mb-1" htmlFor="coordinator-tray-role">
                    {t("flow.pool.filter.tray-role", "Bandeja (rol)")}
                  </label>
                  <select
                    id="coordinator-tray-role"
                    className={`form-select form-select-sm${filters.tray_role_catalog_id ? " border-danger" : ""}`}
                    value={filters.tray_role_catalog_id || ""}
                    onChange={(event) => applyTrayRoleFilter(event.target.value)}
                  >
                    <option value="">{t("flow.cmi.all", "Todas")}</option>
                    {circuitTrayRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {getRoleDisplayLabel(role.name, t)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label mb-1" htmlFor="coordinator-assignment-status">
                    {t("flow.pool.filter.assignment", "Asignación")}
                  </label>
                  <select
                    id="coordinator-assignment-status"
                    className={`form-select form-select-sm${filters.assignment_status ? " border-danger" : ""}`}
                    value={filters.assignment_status || ""}
                    onChange={(event) => applyAssignmentStatusFilter(event.target.value)}
                  >
                    <option value="">{t("flow.pool.filter.assignment-all", "Todos")}</option>
                    <option value="assigned">{t("flow.pool.filter.assignment-assigned", "Asignados")}</option>
                    <option value="unassigned">{t("flow.pool.filter.assignment-unassigned", "Sin asignar (pool)")}</option>
                  </select>
                </div>
                <div className="col-12">
                  <p className="small text-muted mb-0">
                    {t(
                      "flow.pool.filter.hint-unpublished-tray",
                      "Filtra por bandeja del circuito (Pre-Gestor, Gestor, Verificador, Editor) y por si el caso está asignado o en el pool de autoasignación."
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        {inboxSurface === "liaison" && (
          <div className="mb-3">
            <div className="btn-group btn-group-sm" role="group" aria-label={t("flow.pool.filter.visibility-group", "Visibilidad")}>
              <button type="button" className={`btn ${filters.visibility_status === "visible" ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => applyVisibilitySlice("visible")}>
                {t("flow.pool.filter.visible", "Visibles")}
              </button>
              <button type="button" className={`btn ${filters.visibility_status === "hidden" ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => applyVisibilitySlice("hidden")}>
                {t("flow.pool.filter.hidden", "No visibles")}
              </button>
            </div>
            <p className="small text-muted mb-0 mt-2">
              {t("flow.pool.filter.hint-liaison", "Solo publicados. Visibles llegan al usuario final; no visibles están pausados. El Enlace no ve casos en flujo ni dados de baja.")}
            </p>
          </div>
        )}

        {showTrayTabs && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <div className="btn-group" role="group" aria-label={t("specialist.selection.claim-pool.tabs", "Pestañas de la bandeja")}>
            {showMineTab && (
              <button
                type="button"
                className={`btn btn-sm ${trayView === TRAY_VIEW.MINE ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => selectTrayView(TRAY_VIEW.MINE)}
              >
                {t("specialist.selection.claim-pool.inbox", "Mi bandeja")}
              </button>
            )}
            {showPoolTab && (
              <button
                type="button"
                className={`btn btn-sm ${trayView === TRAY_VIEW.POOL ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => selectTrayView(TRAY_VIEW.POOL)}
              >
                {t("specialist.selection.claim-pool.pool", "Bandeja de Autoasignación")}
              </button>
            )}
            <button
              type="button"
              className={`btn btn-sm ${trayView === TRAY_VIEW.GENERAL ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => selectTrayView(TRAY_VIEW.GENERAL)}
            >
              {t("specialist.selection.claim-pool.general", "Bandeja General")}
            </button>
          </div>
          {inboxSurface === "pre-manager" && (
            <div className="btn-group btn-group-sm" role="group" aria-label={t("specialist.selection.pre-kind.group", "Tipo de registro")}>
              <button
                type="button"
                className={`btn ${recordKind === "term" ? "btn-secondary" : "btn-outline-secondary"}`}
                onClick={() => setRecordKind("term")}
              >
                {t("specialist.feeder.term.phase", "Términos Clave")}
              </button>
              <button
                type="button"
                className={`btn ${recordKind === "dri" ? "btn-secondary" : "btn-outline-secondary"}`}
                onClick={() => setRecordKind("dri")}
              >
                {t("specialist.selection.pre-kind.dri", "DRI")}
              </button>
              <button
                type="button"
                className={`btn ${recordKind === "all" ? "btn-secondary" : "btn-outline-secondary"}`}
                onClick={() => setRecordKind("all")}
              >
                {t("flow.cmi.all", "Todos")}
              </button>
            </div>
          )}
          {["pre-manager", "manager", "validator", "editor"].includes(inboxSurface) && (
            <MassiveModuleDownloadButton surface={inboxSurface} process={filters.process} />
          )}
          </div>
        )}

        {showTrayTabs && (
          <p className="small text-muted mb-3">
            {inboxSurface === "pre-manager" && recordKind === "term"
              ? (trayView === TRAY_VIEW.POOL
                ? t(
                  "specialist.selection.pre-kind.hint-term-pool",
                  "Términos clave sin responsable. Reclámalos para pasarlos a Mi bandeja."
                )
                : trayView === TRAY_VIEW.GENERAL
                  ? t(
                    "specialist.selection.pre-kind.hint-term-general",
                    "Términos clave que ya tienen responsable, incluidos los tuyos."
                  )
                  : t(
                    "specialist.selection.pre-kind.hint-term",
                    "Términos clave asignados a tu usuario."
                  ))
              : inboxSurface === "pre-manager" && recordKind === "all"
                ? t(
                  "specialist.selection.pre-kind.hint-all",
                  "Términos clave y DRI de esta bandeja."
                )
                : trayView === TRAY_VIEW.POOL
              ? t(
                "specialist.selection.claim-pool.hint-pool",
                "Casos de ida o devolución sin usuario asignado. Reclama para pasarlos a Mi bandeja."
              )
              : trayView === TRAY_VIEW.GENERAL
                ? t(
                  "specialist.selection.claim-pool.hint-general",
                  "Todos los casos de esta bandeja que ya tienen responsable, incluidos los tuyos. El Gestor consulta; el Coordinador opera aquí."
                )
                : t(
                  "specialist.selection.claim-pool.hint-mine",
                  "Solo los casos asignados a tu usuario en el rol de esta bandeja."
                )}
          </p>
        )}

        {showMass && (
          <FlowCaseMassToolbar
            records={cases}
            selectedRecords={selectedRecords}
            onSelectionChange={setSelectedRecords}
            onAfterAction={refreshAll}
            roleId={roleId}
            poolSurface={inboxSurface}
            roleKey={inboxSurface}
            subjectKind={selectedRecords[0]?.subject_kind || ""}
          />
        )}

        <div className="table-responsive mb-4">
          <table className="table table-sm table-hover">
            <thead>
              <tr>
                {showMass && (
                  <th style={{ width: "40px" }}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={togglePage}
                      disabled={!cases.length}
                    />
                  </th>
                )}
                <th>{t("flow.cmi.col.module", "Módulo")}</th>
                <th>{t("flow.cmi.col.name", "Nombre")}</th>
                <th>{t("general.flow-engine.table.header-code", "Código")}</th>
                <th>{t("flow.cmi.col.state", "Estado")}</th>
                <th>{t("flow.cmi.col.phase", "Fase")}</th>
                {showCoordinatorUnpublishedFilters && (
                  <th>{t("flow.cmi.col.tray", "Bandeja")}</th>
                )}
                <th>{t("flow.cmi.user", "Usuario")}</th>
                <th>{t("general.flow-engine.table.header-updated", "Actualizado")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={(showMass ? 9 : 8) + (showCoordinatorUnpublishedFilters ? 1 : 0)} className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : cases.length === 0 ? (
                <tr>
                  <td colSpan={(showMass ? 9 : 8) + (showCoordinatorUnpublishedFilters ? 1 : 0)} className="text-center text-muted py-4">
                    {t("specialist.selection.pool.empty", "No hay registros con los filtros seleccionados.")}
                  </td>
                </tr>
              ) : (
                cases.map((row) => (
                  <tr key={flowCaseKey(row)}>
                    {showMass && (
                      <td>
                        {canOperateRecord(row) ? (
                          <input
                            type="checkbox"
                            checked={selectedKeys.has(flowCaseKey(row))}
                            onChange={() => toggleRow(row)}
                          />
                        ) : null}
                      </td>
                    )}
                    <td>{getModuleDisplayLabel(row.process, t)}</td>
                    <td>
                      {row.process === "vision" ? (
                        <TrayVisionName
                          text={row.name}
                          onOpen={() => openFicha(row)}
                        />
                      ) : (
                        row.name
                      )}
                    </td>
                    <td>{row.code || row.id}</td>
                    <td>
                      <FlowStateSemaphore stateCatalogId={row.state_catalog_id} interactive={false} />
                      <span className="ms-2 small">{stateLabel(row.state_catalog_id)}</span>
                      {row.published_at && row.is_visible === false && (
                        <span className="badge bg-secondary ms-2">
                          {t("general.flow-engine.paused-badge", "No visible")}
                        </span>
                      )}
                    </td>
                    <td>
                      {row.subject_kind === "search_key_term" ? (
                        <span className="badge bg-warning">
                          {t("specialist.feeder.term.phase", "Término Clave")}
                        </span>
                      ) : (() => {
                        const phase = row.case_phase || resolveCasePhase(row, { process: row.process });
                        const meta = getCasePhaseMeta(phase);
                        return (
                          <span className={`badge bg-${meta.tone}`}>
                            {t(meta.key, meta.fallback)}
                          </span>
                        );
                      })()}
                    </td>
                    {showCoordinatorUnpublishedFilters && (
                      <td>{trayLabel(row)}</td>
                    )}
                    <td>{userLabel(row.current_user_id)}</td>
                    <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}</td>
                    <td className="text-nowrap">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        title={t("buttons.view", "Ver")}
                        onClick={() => openFicha(row)}
                      >
                        <i className="fa fa-eye" />
                      </button>
                      {!isPoolView && canOperateRecord(row) && (
                        <RecordExcelUpdateRowButton
                          record={row}
                          moduleProcess={row.process}
                          onApplied={refreshAll}
                          tooltipScope={`${row.process}-${row.id}`}
                          className="ms-1"
                          subjectKind={row.subject_kind || ""}
                        />
                      )}
                      {canDeleteRecord(row) && !isPoolView && (
                        <button
                          type="button"
                          className="table-button-delete ms-1"
                          title={t("buttons.delete", "Eliminar")}
                          onClick={() => handleDeleteRecord(row)}
                        >
                          <i className="fa fa-trash" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
            <div className="small text-muted">{t("flow.cmi.results", "{{count}} resultados", { count: total })}</div>
            <div className="d-flex align-items-center gap-2">
              <span className="small">{t("flow.cmi.per-page", "Registros por página")}</span>
              <Select
                listOptions={PAGE_SIZE_OPTIONS}
                onChangeSelect={onChangeSelect}
                pageSize={itemsPerPage}
              />
            </div>
          </div>
          <div className="pagination justify-content-center mt-2">
            <Pagination
              totalPages={Math.ceil((total || 0) / (itemsPerPage || 20))}
              totalItems={total}
              currentPage={(page || 0) + 1}
              itemsPerPage={itemsPerPage}
              onPageChange={onPageChange}
            />
          </div>
        </div>

        {showMass && !isPoolView && (
          <FlowMassReassignPanel
            records={selectedRecords}
            users={reassignUsers}
            roleKey={selectedTrayRole?.name || normalizedRole}
            activeRoleKey={selectedRoleKey}
            canReassign={canReassign}
            loading={Boolean(actionLoading.assign)}
            currentUserId={authUserId}
            onAssigned={refreshAll}
          />
        )}

      </div>
    </>
  );
};

SpecialistAssignmentBoard.propTypes = {
  roleKey: PropTypes.oneOf([
    "pre-manager",
    "manager",
    "validator",
    "editor",
    "coordinator",
    "liaison",
    RolesEnum.SPECIALIST_PRE_MANAGER,
    RolesEnum.SPECIALIST_MANAGER,
    RolesEnum.SPECIALIST_VALIDATOR,
    RolesEnum.SPECIALIST_EDITOR,
    RolesEnum.SPECIALIST_COORDINATOR,
    RolesEnum.SPECIALIST_LIAISON
  ]).isRequired,
  titleKey: PropTypes.string.isRequired,
  defaultTitle: PropTypes.string.isRequired,
  consultativeMode: PropTypes.bool
};

export default SpecialistAssignmentBoard;
